import { useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, History, Eye } from "lucide-react";
import { useApi, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useScoreGate } from "./ScoreGate";
import { DatePicker } from "./DateField";

const MAX_MB = 20;
const fileError = (file) => {
  if (!["application/pdf", "image/png", "image/jpeg"].includes(file.type)) return "Only PDF, JPG or PNG files are allowed";
  if (file.size > MAX_MB * 1024 * 1024) return `File is too large (max ${MAX_MB} MB)`;
  return null;
};

const open = async (api, base, idx) => {
  try { const r = await api.get(`${base}/document?idx=${idx}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch { toast.error("No document stored"); }
};

export const LicenceTools = ({ lic, reload, base = `/licenses/${lic.id}` }) => {
  const api = useApi();
  const [mode, setMode] = useState(null);
  const [f, setF] = useState({});
  const gate = useScoreGate();
  const upload = async (file) => {
    if (!file) return;
    const bad = fileError(file);
    if (bad) return toast.error(bad);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`${base}/document`, fd); toast.success("Licence copy attached"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const renew = async (e) => {
    e.preventDefault();
    const bad = f.file && fileError(f.file);
    if (bad) return toast.error(bad);
    const send = (r) => {
      const fd = new FormData();
      ["expiry_date", "issued_on", "license_number", "fee"].forEach((k) => f[k] && fd.append(k, f[k]));
      if (f.file) fd.append("file", f.file);
      if (r) fd.append("score_override_reason", r);
      return api.post(`${base}/renew`, fd);
    };
    if (await gate.run(send)) { toast.success("Licence renewed; old record kept in history"); setMode(null); setF({}); reload(); }
  };
  const btn = "p-1.5 text-slate-400 hover:text-white";
  return (
    <span className="inline-flex items-center align-middle" data-testid="license-row-tools">
      {gate.dialog}
      {lic.document_path && <button className={btn} title="View copy" onClick={() => open(api, base, -1)} data-testid="license-view-doc-btn"><Eye size={14} /></button>}
      <label className={`${btn} cursor-pointer inline-flex`} title="Attach licence copy" data-testid="license-upload-doc-btn"><Upload size={14} />
        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} /></label>
      <button className={btn} title="Renew" onClick={() => setMode("renew")} data-testid="license-renew-btn"><RefreshCw size={14} /></button>
      <button className={btn} title="History" onClick={() => setMode("history")} data-testid="license-history-btn"><History size={14} /></button>
      {mode && (
        <Dialog open onOpenChange={() => setMode(null)}>
          <DialogContent className="bg-[#121824] border-[#1F2A3E] max-w-lg" data-testid={`license-${mode}-dialog`}>
            <DialogHeader><DialogTitle className="font-display text-xl">{mode === "renew" ? "Renew" : "History"}: {lic.label || lic.license_type}</DialogTitle></DialogHeader>
            {mode === "renew" ? (
              <form onSubmit={renew} className="grid gap-3 text-left">
                <p className="text-xs text-slate-400">Current: {lic.license_number} · expires {lic.expiry_date}. The current details and copy move to history.</p>
                {[["expiry_date", "New expiry date *", "date"], ["issued_on", "Issued on", "date"], ["license_number", "New licence number (if changed)", "text"], ["fee", "Renewal fee paid (₹)", "number"]].map(([k, l, t]) => (
                  <label key={k} className="grid gap-1.5"><span className="text-xs text-slate-400">{l}</span>
                    {t === "date" ? <DatePicker value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} required={k === "expiry_date"} fromYear={2000} testid={`renew-${k}`} />
                      : <input type={t} className="c-input" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`renew-${k}`} />}</label>))}
                <label className="grid gap-1.5"><span className="text-xs text-slate-400">Renewed licence copy (PDF/JPG/PNG, max 20 MB)</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setF({ ...f, file: e.target.files[0] })} data-testid="renew-file" className="text-sm" /></label>
                <Button type="submit" data-testid="renew-submit" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold">Save renewal</Button>
              </form>
            ) : (
              <div className="grid gap-2 text-sm text-left" data-testid="license-history-list">
                <div className="rounded-lg border border-[#00E599]/30 p-3"><b>Current</b> · {lic.license_number} · {lic.issued_on || "—"} → {lic.expiry_date}</div>
                {[...(lic.history || [])].map((h, i) => ({ h, i })).reverse().map(({ h, i }) => (
                  <div key={i} className="rounded-lg border border-[#1F2A3E] p-3 flex justify-between items-center gap-2" data-testid="license-history-row">
                    <span>{h.license_number} · {h.issued_on || "—"} → {h.expiry_date}<span className="block text-[11px] text-slate-500">Renewed {h.renewed_at?.slice(0, 10)} by {h.renewed_by}</span></span>
                    {h.document_path && <button className="text-xs text-[#00E599]" onClick={() => open(api, base, i)} data-testid="license-history-doc">View copy</button>}
                  </div>))}
                {!lic.history?.length && <p className="text-xs text-slate-500">No past renewals yet.</p>}
              </div>
            )}
          </DialogContent>
        </Dialog>)}
    </span>
  );
};
